import type { AnyServerRouteMethod, KeysOf, UseFetchOptions } from 'nuxt/app'

// nuxt 4.6 defaults the UseFetchOptions method generic to 'get' only
type KftApiFetchOptions<T, Y> = UseFetchOptions<
  T,
  Y,
  KeysOf<Y>,
  undefined,
  string,
  AnyServerRouteMethod
>

export function useKftApiFetch<T, Y = T>(
  url: string,
  options: KftApiFetchOptions<T, Y> = {}
) {
  const config = useRuntimeConfig()
  const { $i18n } = useNuxtApp()
  const { locale: ISOLocale } = $i18n
  const locale = convertISO2Locale(ISOLocale.value)
  const localeBCP47 = convertLocaleIntoBCP47(ISOLocale.value)
  const { apiBaseURL } = config.public

  const nuxt = useNuxtApp()

  let key = url
  if (key[0] === '/') {
    // remove the leading slash so that google does not crawl unwanted urls
    key = key.slice(1)
  }

  const defaults: KftApiFetchOptions<T, Y> = {
    baseURL: apiBaseURL,
    // cache request
    key,

    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': `${localeBCP47},${locale}`,
    },
    dedupe: 'cancel',
    onResponse(response) {
      const customOnResponse = options.onResponse
      delete options.onResponse

      if (customOnResponse && typeof customOnResponse === 'function') {
        customOnResponse(response)
      }
    },
    getCachedData(key) {
      return nuxt.isHydrating ? nuxt.payload.data[key] : nuxt.static.data[key]
    },
  }

  const fetchOptions = { ...defaults, ...options }

  return useFetch(url, fetchOptions)
}
